export type PersonalisedQuoteContentKey =
  | 'generic'
  | 'washer'
  | 'dryer'
  | 'dishwasher'
  | 'refrigerator'
  | 'range'
  | 'cooktop'
  | 'wallOven'
  | 'standMixer'
  | 'microwave'
  | 'freezer';

type PersonalisedQuoteApplianceContent = {
  repairCost?: number;
  repairBody: string;
  showRepairEstimates: boolean;
  brands?: string[];
};

export const contentKeyByApplianceCode: Record<string, PersonalisedQuoteContentKey> = {
  WSHR: 'washer',
  DRYR: 'dryer',
  DISH: 'dishwasher',
  RFRG: 'refrigerator',
  RNGE: 'range',
  CKTP: 'cooktop',
  WLOV: 'wallOven',
  STMX: 'standMixer',
  MCRO: 'microwave',
  FRZR: 'freezer',
};

const cookingRepairBody =
  'Including fan damage caused by grease build-up, broken control knobs and door handles, and cracked glass doors';

const refrigerationRepairBody =
  'Including damaged shelves and drawers, ice-maker or dispenser damage and overheated compressors due to restricted airflow or blocked vents';

export const contentByKey: Record<PersonalisedQuoteContentKey, PersonalisedQuoteApplianceContent> =
  {
    generic: {
      repairBody:
        'Including spills, drops, cracks, leaks, and other accidental damage from handling',
      showRepairEstimates: false,
    },
    washer: {
      repairCost: 385,
      repairBody:
        'Including blocked filters leading to overheating, damaged drum components caused by foreign objects, and cracked detergent drawers/doors',
      showRepairEstimates: true,
      brands: ['WHIRL', 'MYTAG', 'AMANA'],
    },
    dryer: {
      repairCost: 350,
      repairBody:
        'Including lint-filter blockages causing overheating, damaged doors and hinges, and motor strain caused by overloaded loads',
      showRepairEstimates: true,
      brands: ['WHIRL', 'MYTAG', 'AMANA'],
    },
    dishwasher: {
      repairCost: 450,
      repairBody:
        'Including blocked pumps from food debris, damaged spray arms, and cracked racks or baskets from heavy use',
      showRepairEstimates: true,
    },
    refrigerator: {
      repairCost: 360,
      repairBody: refrigerationRepairBody,
      showRepairEstimates: true,
    },
    range: {
      repairCost: 338,
      repairBody: `${cookingRepairBody} or cooktop surfaces`,
      showRepairEstimates: true,
    },
    cooktop: {
      repairCost: 385,
      repairBody: 'Including broken control knobs and cracked cooktop surfaces',
      showRepairEstimates: true,
    },
    wallOven: { repairCost: 450, repairBody: cookingRepairBody, showRepairEstimates: true },
    standMixer: {
      repairBody:
        'Including motor damage from heavy use, damaged accessories/attachments and cracks from drops',
      showRepairEstimates: true,
      brands: ['KHAID'],
    },
    microwave: {
      repairCost: 260,
      repairBody:
        'Including cracked doors/handles, damaged turntables and overheating caused by blocked vents',
      showRepairEstimates: true,
    },
    freezer: { repairCost: 305, repairBody: refrigerationRepairBody, showRepairEstimates: true },
  };

// Brands that show the generic content for every appliance.
export const genericOnlyBrands = ['JNAIR'];

const WHIRLPOOL_REPAIR_ESTIMATES_URL = 'https://www.whirlpool.com/repair/service.html';

export const defaultRepairEstimatesUrl = WHIRLPOOL_REPAIR_ESTIMATES_URL;

export const repairEstimatesUrlByBrand: Record<string, string> = {
  WHIRL: WHIRLPOOL_REPAIR_ESTIMATES_URL,
  MYTAG: 'https://www.maytag.com/repair-or-replace/service.html',
  KHAID: 'https://www.kitchenaid.com/repair-or-replace/service.html',
  AMANA: WHIRLPOOL_REPAIR_ESTIMATES_URL,
};

export const personalisedQuoteCopy = {
  header:
    "Enhanced Protection for damage caused by accidents and everyday use - not covered by your manufacturer's warranty",
  choice: 'Choose your protection',
  choiceError: 'Please select your coverage level',
  warrantyHeader: "Manufacturer's Warranty Only",
  applianceCareSubcopy: 'Monthly plan. Cancel anytime with no fee',
  replacementBody: "If it's beyond repair, we'll replace it (delivery included)",
  repairTitle: 'Repairs for damage caused by accidents and everyday use',
  unlimitedTitle: 'Unlimited repairs by expert technicians',
  unlimitedBody: '$0 deductible on service calls, parts, and labor, even if no problem is found',
  ongoingTitle: 'Ongoing coverage for breakdowns',
  ongoingBody: "During and after your manufacturer's warranty",
  back: 'Go back',
  cta: 'Choose your protection to continue',
  ctaWarranty: "Continue with Manufacturer's Warranty Only",
  ctaApplianceCare: 'Continue with Appliance Care',
  upsellModal: {
    noCostHeader:
      'You may have to pay a repair cost where no manufacturing defect is found e.g. accidental damage',
    unlimitedBenefit: '$0 deductible on unlimited repairs by expert technicians',
    noCommitment: 'No long-term commitment',
    laterNote:
      'You can buy this protection later too – but the rate goes up as your appliance ages.',
    bestPrice: "This is the best price you'll ever get it at",
    registerOnly: 'No, register only',
  },
} as const;
